import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  UserStatusService,
  StatusInfo,
} from '../header/statistics-dropdown/user-status.service';
import { UserService } from 'src/app/services/core/user.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-avatar-group',
    templateUrl: './avatar-group.component.html',
    styleUrls: ['./avatar-group.component.scss'],
    standalone: false
})
export class AvatarGroupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() avatars: any[] = [];
  @Input() maxDisplay: number = 2;
  @Input() showStatusIndicator: boolean = false;
  @Input() widthSize: string = '40px';
  @Input() heightSize: string = '40px';
  @Input() mergeDuplicates: boolean = false;
  @Input() useDetailedTooltip: boolean = false;
  @Input() enableClickTooltip: boolean = false;
  @Input() showHoverLabel: boolean = true;
  loginUserID: any;
  normalizedAvatars: any[] = [];
  remainingAvatars: any[] = [];
  selectedAvatarForMenu: any = null;
  processedAvatars: any[] = [];
  processedRemainingAvatars: any[] = [];
  currentStatus: any = null;
  public statusSubscription: Subscription | null = null;
  currentStatusInfo: StatusInfo | null = null;
  public statusSvg: SafeHtml | undefined;

  constructor(
    public userStatusService: UserStatusService,
    private sanitizer: DomSanitizer,
    private currentUser: UserService
  ) {}

  ngOnInit(): void {
    this.loginUserID = this.currentUser.getID();
    this.getUserCurrentStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['avatars'] ||
      changes['maxDisplay'] ||
      changes['mergeDuplicates'] ||
      changes['useDetailedTooltip']
    ) {
      this.processAvatarsWithStatus();
    }
  }

  private processAvatarsWithStatus(): void {
    const sourceAvatars = this.normalizeAvatars(this.avatars || []);
    this.normalizedAvatars = sourceAvatars;

    this.processedAvatars = sourceAvatars
      .slice(0, this.maxDisplay)
      .map((avatar) => {
        const statusSvg = this.calculateStatusSvg(avatar);
        return { ...avatar, statusSvg };
      });

    this.processedRemainingAvatars = sourceAvatars
      .slice(this.maxDisplay)
      .map((avatar) => {
        const statusSvg = this.calculateStatusSvg(avatar);
        return { ...avatar, statusSvg };
      });
  }

  private normalizeAvatars(avatars: any[]): any[] {
    const enrichedAvatars = avatars.map((avatar, index) =>
      this.enrichAvatar(avatar, index)
    );

    if (!this.mergeDuplicates) {
      return enrichedAvatars;
    }

    const avatarMap = new Map<string, any>();

    enrichedAvatars.forEach((avatar, index) => {
      const avatarKey = avatar.uniqueKey || this.getAvatarKey(avatar, index);
      const existingAvatar = avatarMap.get(avatarKey);

      if (existingAvatar) {
        existingAvatar.roles = this.mergeRoles(
          existingAvatar.roles,
          avatar.roles
        );
        existingAvatar.tooltipText = this.buildAvatarTooltip(existingAvatar);
        existingAvatar.url = existingAvatar.url || avatar.url;
        existingAvatar.staffID = existingAvatar.staffID || avatar.staffID;
        existingAvatar.userStatus =
          existingAvatar.userStatus || avatar.userStatus;
        existingAvatar.email = existingAvatar.email || avatar.email;
        return;
      }

      avatarMap.set(avatarKey, { ...avatar });
    });

    return Array.from(avatarMap.values());
  }

  private enrichAvatar(avatar: any, index: number): any {
    const name = (avatar?.name || '').trim();
    const roles = this.extractRoles(avatar);
    const uniqueKey = this.getAvatarKey(
      {
        ...avatar,
        name,
      },
      index
    );

    return {
      ...avatar,
      _avatarIndex: index,
      name,
      roles,
      uniqueKey,
      tooltipText: this.buildAvatarTooltip({ ...avatar, name, roles }),
    };
  }

  private extractRoles(avatar: any): string[] {
    const roleCandidates = Array.isArray(avatar?.roles)
      ? avatar.roles
      : [avatar?.role, avatar?.roleLabel];

    return this.mergeRoles([], roleCandidates);
  }

  private mergeRoles(
    existingRoles: string[] = [],
    nextRoles: string[] = []
  ): string[] {
    const mergedRoles: string[] = [];
    const seenRoles = new Set<string>();

    [...existingRoles, ...nextRoles].forEach((role) => {
      if (!role || typeof role !== 'string') {
        return;
      }

      const trimmedRole = role.trim();
      if (!trimmedRole) {
        return;
      }

      const roleKey = trimmedRole.toLowerCase();
      if (seenRoles.has(roleKey)) {
        return;
      }

      seenRoles.add(roleKey);
      mergedRoles.push(trimmedRole);
    });

    return mergedRoles;
  }

  private getAvatarKey(avatar: any, index: number): string {
    if (avatar?.staffID) {
      return `staff:${avatar.staffID}`;
    }

    if (avatar?.email) {
      return `email:${String(avatar.email).toLowerCase()}`;
    }

    if (avatar?.name) {
      return `name:${String(avatar.name).trim().toLowerCase()}`;
    }

    return `avatar:${index}`;
  }

  private buildAvatarTooltip(avatar: any): string {
    if (avatar?.tooltip) {
      return avatar.tooltip;
    }

    const name = (avatar?.name || '').trim();
    const roles = Array.isArray(avatar?.roles)
      ? this.mergeRoles([], avatar.roles)
      : this.extractRoles(avatar);

    if (!this.useDetailedTooltip || !roles.length) {
      return name;
    }

    return `${name} is ${this.formatRoles(roles)}`;
  }

  private formatRoles(roles: string[]): string {
    if (roles.length === 1) {
      return roles[0];
    }

    if (roles.length === 2) {
      return `${roles[0]} and ${roles[1]}`;
    }

    return `${roles.slice(0, -1).join(', ')}, and ${roles[roles.length - 1]}`;
  }

  private calculateStatusSvg(avatar: any): SafeHtml | undefined {
    const statusToShow = avatar?.userStatus || 'available';
    const status = this.userStatusService?.getStatusBySlug(statusToShow);

    if (status?.profileShownSvg) {
      return this.sanitizer.bypassSecurityTrustHtml(status.profileShownSvg);
    }

    return undefined;
  }

  getUserCurrentStatus() {
    this.currentStatus = this.currentUser.getUserStatus();
    this.updateStatusInfo();

    this.statusSubscription = this.userStatusService.status$.subscribe(
      (statusInfo) => {
        if (statusInfo) {
          this.currentStatusInfo = statusInfo;
          this.currentStatus = this.currentStatusInfo?.slug;
          this.updateStatusInfo();
          this.processAvatarsWithStatus();
        }
      }
    );
  }

  private updateStatusInfo(): void {
    const status = this.userStatusService?.getStatusBySlug(this.currentStatus);
    if (status?.profileShownSvg) {
      this.statusSvg = this.sanitizer.bypassSecurityTrustHtml(
        status.profileShownSvg
      );
    } else {
      this.statusSvg = undefined;
    }
  }

  get remainingCount(): number {
    return Math.max(0, this.normalizedAvatars.length - this.maxDisplay);
  }

  get displayedAvatars(): any[] {
    return this.processedAvatars.length > 0
      ? this.processedAvatars
      : this.normalizedAvatars.slice(0, this.maxDisplay);
  }

  onMoreClick(): void {
    this.remainingAvatars =
      this.processedRemainingAvatars.length > 0
        ? this.processedRemainingAvatars
        : this.normalizedAvatars.slice(this.maxDisplay);
    this.selectedAvatarForMenu = null;
  }

  getInitials(name: string): string {
    if (!name) return '';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  getAvatarTooltip(avatar: any): string {
    return avatar?.tooltipText || avatar?.name || '';
  }

  getAvatarRolesLabel(avatar: any): string {
    return this.extractRoles(avatar).join(', ');
  }

  onAvatarClick(avatar: any, event: Event): void {
    if (!this.enableClickTooltip) {
      return;
    }

    event.stopPropagation();
    const tooltipText = this.getAvatarTooltip(avatar);
    if (!tooltipText) {
      return;
    }

    this.selectedAvatarForMenu = avatar;
  }

  onMenuItemClick(action: (() => void) | undefined, event: Event) {
    event.stopPropagation();
    this.selectedAvatarForMenu = null;
    if (typeof action === 'function') {
      action();
    }
  }

  ngOnDestroy(): void {
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
  }

  onMenuClosed() {
    this.selectedAvatarForMenu = null;
  }

  get dynamicFontSize(): string {
    const baseSize = 14;
    const referenceSize = 40;

    const width = parseInt(this.computedWidth, 10) || referenceSize;
    return `${(baseSize * width) / referenceSize}px`;
  }

  get computedWidth(): string {
    return this.widthSize || '40px';
  }

  get computedHeight(): string {
    return this.heightSize || '40px';
  }

  /**
   * Matches the header's own logged-in-user avatar badge (app-profile-avatar,
   * profile-avatar.component.scss: `.online_circle { bottom: -3px; right:
   * -4px; }` on its actual 37px rendered avatar).
   *
   * Rather than scaling that raw -4px/-3px by (thisAvatarSize / 37) — which
   * measurably overshoots the header's own overhang ratio at sizes above
   * 37px (verified live: at 40px that scaling produced ~10.8% right /
   * ~8.1% bottom overhang vs. the header's actual measured ~9.1% / ~6.4%,
   * i.e. visibly more "detached" than the header) — this instead reuses the
   * header's measured overhang as a FRACTION of avatar size (getBoundingClientRect
   * on both live elements: right overhang 3.37px / 37px ≈ 9.1%, bottom
   * 2.37px / 37px ≈ 6.4%) and applies that same fraction to this avatar's
   * own size. This keeps the badge's overlap looking identical to the
   * header's at every size this component renders at (Bids/Tasks/Tickets/
   * Customers lists, etc.), not just at 37px.
   */
  private static readonly STATUS_ICON_RIGHT_OVERHANG_FRACTION = 0.091;
  private static readonly STATUS_ICON_BOTTOM_OVERHANG_FRACTION = 0.064;

  get dynamicStatusIconRight(): string {
    const width = parseInt(this.computedWidth, 10) || 37;
    return `${-(width * AvatarGroupComponent.STATUS_ICON_RIGHT_OVERHANG_FRACTION)}px`;
  }

  get dynamicStatusIconBottom(): string {
    const height = parseInt(this.computedHeight, 10) || 37;
    return `${-(height * AvatarGroupComponent.STATUS_ICON_BOTTOM_OVERHANG_FRACTION)}px`;
  }

  get dynamicMoreButtonSize(): string {
    return this.computedWidth;
  }

  get dynamicMoreButtonFontSize(): string {
    const baseSize = 12;
    const referenceSize = 40;
    const width = parseInt(this.computedWidth, 10) || referenceSize;
    return `${(baseSize * width) / referenceSize}px`;
  }
}
